// Board/card composition adapted from Atomic CRM DealListContent, DealColumn and DealCard (MIT).
// Upstream commit 85af317881c52481902c499e525de98be7a619d6; see /third-party/atomic-crm-MIT.txt.
import { useEffect, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import type { AutomationCase } from "@rutamagica/contracts";
import { useAuth } from "../../auth/AuthProvider";
import {
  Button,
  SearchField,
  SelectField,
  StatePanel,
  StatusBadge,
} from "../../components/ui";
import { automationApi, type CasePage } from "../../data/crm-automation";
import { viewCache } from "../../data/view-cache";
import { formatDate, formatDateTime } from "../../data/format";
import type { ServiceType } from "../../data/types";
import { caseUrl, copyLabel } from "./shared";
import { commercialStages, getCasesByStage } from "./automation-board";
import { ServiceTag } from "./ServiceTag";
import { MessageCircle } from "lucide-react";
export const treatmentLabels = {
  unclassified: "Sin clasificar",
  informational: "Solo consulta",
  sales_interest: "Interés de compra",
};
const pipelineCache = viewCache<CasePage>();
/** What the funnel shows; open sales by default, the board then has only their four columns. */
/** Each scope asks the server for these stages and shows these columns (a won sale is Apartado, Pagado or Realizado). */
const scopes: Array<{ id: string; label: string; stages?: string[]; columns?: string[] }> = [
  { id: "open", label: "En proceso", stages: ["new", "conversation", "quote", "awaiting", "won"], columns: ["new", "conversation", "quote", "awaiting", "won", "paid"] },
  { id: "inquiries", label: "Esperan tu respuesta" },
  { id: "won", label: "Apartados y realizados", stages: ["won"], columns: ["won", "paid", "done"] },
  { id: "lost", label: "Perdidas o archivadas", stages: ["lost"] },
  { id: "all", label: "Todo" },
];
export function AutomationPipeline({
  service,
  defaultLayout = "list",
}: {
  service?: ServiceType | undefined;
  defaultLayout?: "list" | "board";
}) {
  const { hasPermission } = useAuth();
  const canRead = hasPermission("crm.read");
  const [params, setParams] = useSearchParams(),
    location = useLocation();
  const [result, setResult] = useState<CasePage | null>(null),
    [error, setError] = useState(""),
    [loading, setLoading] = useState(true),
    [revision, setRevision] = useState(0);
  const layoutParam = params.get("layout"),
    layout =
      layoutParam === "board" || layoutParam === "list"
        ? layoutParam
        : defaultLayout,
    scope = scopes.some((item) => item.id === params.get("scope"))
      ? params.get("scope")!
      : "open",
    query = params.get("q") ?? "",
    page = Math.max(1, Number(params.get("page")) || 1),
    perPage = layout === "board" ? 100 : 25;
  const [search, setSearch] = useState(query);
  useEffect(() => {
    const timer = window.setTimeout(() => setSearch(query), 300);
    return () => window.clearTimeout(timer);
  }, [query]);
  const filterQuery = new URLSearchParams({
    page: String(page),
    per_page: String(perPage),
  });
  if (service) filterQuery.set("service", service);
  if (search) filterQuery.set("q", search);
  const scopeStages = scopes.find((item) => item.id === scope)?.stages;
  if (scopeStages) filterQuery.set("stages", scopeStages.join(","));
  if (scope === "inquiries") filterQuery.set("awaiting_reply", "true");
  const key = filterQuery.toString();
  useEffect(() => {
    if (!canRead) return;
    let active = true;
    const cached = pipelineCache.get(key);
    if (cached) setResult(cached);
    setLoading(!cached);
    setError("");
    automationApi
      .cases(new URLSearchParams(key))
      .then((x) => {
        pipelineCache.set(key, x);
        if (active) setResult(x);
      })
      .catch((e) => {
        if (active) setError(e.message);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [key, canRead, revision]);
  const update = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    if (key !== "page") next.delete("page");
    setParams(next, { replace: true });
  };
  if (!canRead) return <StatePanel state="forbidden" />;
  const cards = result?.data ?? [],
    grouped = getCasesByStage(cards),
    total = result?.meta.total ?? 0;
  return (
    <section className="workspace-card automation-pipeline" aria-label="Ventas en curso">
      <div className="workspace-filters">
        <SearchField
          value={query}
          onChange={(v) => update("q", v)}
          placeholder="Buscar cliente o referencia..."
        />
        <SelectField
          label="Mostrar"
          value={scope}
          onChange={(e) => update("scope", e.target.value)}
        >
          {scopes.map((item) => (
            <option key={item.id} value={item.id}>
              {item.label}
            </option>
          ))}
        </SelectField>
        <div className="case-actions pipeline-layout" aria-label="Presentación">
          <Button
            variant={layout === "board" ? "primary" : "secondary"}
            aria-pressed={layout === "board"}
            onClick={() => update("layout", "board")}
          >
            Tablero
          </Button>
          <Button
            variant={layout === "list" ? "primary" : "secondary"}
            aria-pressed={layout === "list"}
            onClick={() => update("layout", "list")}
          >
            Lista
          </Button>
        </div>
      </div>
      {error ? (
        <StatePanel
          state="error"
          description={error}
          onRetry={() => setRevision((v) => v + 1)}
        />
      ) : loading ? (
        <StatePanel state="loading" />
      ) : (
        <>
          <p className="automation-meta">
            {total === 1 ? "1 cliente" : `${total} clientes`}
            {total > perPage
              ? ` · mostrando ${(page - 1) * perPage + 1}–${Math.min((page - 1) * perPage + cards.length, total)}`
              : ""}
          </p>
          {layout === "board" ? (
            <div
              className="automation-board"
              role="region"
              aria-label="Tablero comercial"
            >
              {[
                ...(scopes.find((item) => item.id === scope)?.columns ?? scopeStages ?? commercialStages),
                ...(grouped.uncategorized!.length ? ["uncategorized"] : []),
              ].map((s) => (
                <section
                  key={s}
                  className="automation-column"
                  aria-label={
                    s === "uncategorized"
                      ? "Reservas sin venta registrada"
                      : copyLabel(s)
                  }
                >
                  <header>
                    <h3>
                      {s === "uncategorized"
                        ? "Reservas sin venta registrada"
                        : copyLabel(s)}
                    </h3>
                    <span className="count-pill">{grouped[s]!.length}</span>
                  </header>
                  {grouped[s]!.map((c) => (
                    <CaseCard
                      key={c.id}
                      item={c}
                      returnTo={location.pathname + location.search}
                      showStage={false}
                    />
                  ))}
                  {!grouped[s]!.length && (
                    <p className="workspace-empty">Nadie aquí</p>
                  )}
                </section>
              ))}
            </div>
          ) : (
            <div className="automation-list">
              {cards.map((c) => (
                <CaseCard
                  key={c.id}
                  item={c}
                  returnTo={location.pathname + location.search}
                />
              ))}
            </div>
          )}
          {!cards.length && (
            <StatePanel
              state="empty"
              title="Sin expedientes con estos filtros"
            />
          )}
          <div className="workspace-pagination">
            <Button
              variant="quiet"
              disabled={page <= 1}
              onClick={() => update("page", String(page - 1))}
            >
              Anterior
            </Button>
            <span>Página {page}</span>
            <Button
              variant="quiet"
              disabled={page * perPage >= total}
              onClick={() => update("page", String(page + 1))}
            >
              Siguiente
            </Button>
          </div>
        </>
      )}
    </section>
  );
}
/** "hace 5 min", "hace 3 h", "ayer", then the date: how long the conversation has waited. */
export function timeAgo(at: string, now = Date.now()) {
  const minutes = Math.max(0, Math.round((now - Date.parse(at)) / 60000));
  if (minutes < 1) return "ahora";
  if (minutes < 60) return `hace ${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `hace ${hours} h`;
  if (hours < 48) return "ayer";
  return formatDate(at);
}
function CaseCard({
  item: c,
  returnTo,
  showStage = true,
}: {
  item: AutomationCase;
  returnTo: string;
  showStage?: boolean;
}) {
  const next = c.tasks.find((t) => t.status === "pending");
  return (
    <article className="automation-case-card">
      <strong>{c.customerName ?? c.caseLabel}</strong>
      <ServiceTag service={c.service} />
      {c.lastMessage && (
        <p
          className={`funnel-last-message funnel-last-message--${c.lastMessage.direction}`}
          title={c.lastMessage.body}
        >
          <MessageCircle size={14} aria-hidden="true" />
          <span>
            <strong>
              {c.lastMessage.direction === "inbound" ? "Te escribió" : "Respondiste"}
            </strong>{" "}
            {c.lastMessage.body || "(archivo)"}
          </span>
          <small>{timeAgo(c.lastMessage.at)}</small>
        </p>
      )}
      <small>
        {c.commercial.requestedDate
          ? formatDate(c.commercial.requestedDate)
          : "Fecha por definir"}
      </small>
      <div className="automation-meta">
        {showStage && (
          <StatusBadge>
            {c.commercial.stage ? copyLabel(c.commercial.stage) : "Sin etapa"}
          </StatusBadge>
        )}
      </div>
      <p>
        <strong>Siguiente paso:</strong> {c.nextAction}
      </p>
      <small className={next?.dueAt && Date.parse(next.dueAt) < Date.now() ? "text-danger" : ""}>
        {next?.dueAt
          ? `Para el ${formatDateTime(next.dueAt)}`
          : "Sin fecha de seguimiento"}
      </small>
      <Link
        className="button button--secondary"
        to={caseUrl(c.caseRef.kind, c.caseRef.id, returnTo)}
      >
        Abrir
      </Link>
    </article>
  );
}
